import { CommonModule } from '@angular/common';
import {
    Component, ElementRef, EventEmitter, inject, Input, OnChanges, Output,
    signal, SimpleChanges,
} from '@angular/core';
import {
    AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule, MatCheckboxChange } from '@angular/material/checkbox';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { UserDataPickerComponent } from '../selectors/user-data-picker/user-data-picker.component';
import { ConnectionPickerComponent } from '../selectors/connection-picker/connection-picker.component';
import { UserDataService } from '../../services/user-data/user-data.service';

/**
 * Schema-driven form renderer. Given a JSON-schema doc, builds a reactive
 * FormGroup matching its `properties`, picks an Angular Material widget per
 * field by inspecting `(type, x-options.source, enum)`, and emits the
 * resulting value via {@code (valueChange)} on every edit.
 *
 * Widget mapping:
 *   string + x-options.source = "userdata"            → <app-user-data-picker>
 *   string + x-options.source = "userdata-types"      → free-text autocomplete from getUserDataTypes()
 *   string + x-options.source = "userdata-departments"      → free-text autocomplete
 *   string + x-options.source = "userdata-org-units"        → free-text autocomplete
 *   string + x-options.source = "userdata-financial-units"  → free-text autocomplete
 *   string + x-options.source = "connector-catalog"   → placeholder text input (catalog service not yet shipped)
 *   string + enum                                     → <mat-select>
 *   string (else)                                     → <input>
 *   integer / number                                  → <input type="number"> with min/max
 *   boolean                                           → <mat-slide-toggle>
 *   object                                            → recursive nested SchemaFormComponent (rendered as a labeled section)
 *   array of primitives                               → <mat-chip-grid> input
 *
 * Out of scope for v1 (documented as TODO inline at the call site that needs them):
 *   • $ref / allOf / oneOf / anyOf — we own the schemas, none of the in-tree
 *     ones use these constructs.
 *   • array of objects — repeating group with add/remove. None of the
 *     in-tree schemas need it yet.
 *   • additionalProperties as a key-value editor (queryParams / headers) —
 *     the userdata-pull-http schema declares it but the runner reads them
 *     from a free-form JSON for now.
 *
 * The component is intentionally hand-rolled instead of pulling in
 * ngx-formly: our schema dialect is small (~10 widget types), our x-options
 * extensions are bespoke, and ngx-formly's heavyweight type-system would
 * obscure the actual mapping.
 */
@Component({
    selector: 'app-schema-form',
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule,
        MatFormFieldModule, MatInputModule, MatSelectModule, MatSlideToggleModule,
        MatCheckboxModule,
        MatAutocompleteModule, MatButtonModule, MatChipsModule, MatIconModule, MatTooltipModule,
        UserDataPickerComponent, ConnectionPickerComponent,
    ],
    templateUrl: './schema-form.component.html',
    styleUrl: './schema-form.component.css',
})
export class SchemaFormComponent implements OnChanges {

    /** Required. The parsed JSON-schema document. Must be {type: "object"}. */
    @Input({ required: true }) schema!: any;

    /** Optional initial value. Falls back to per-field {@code default} values
     *  declared in the schema, then to nulls. */
    @Input() value: any = null;

    /** Whole-form disabled flag (e.g. read-only mode in an audit view). */
    @Input() disabled = false;

    /** Hierarchical path from the document root to this form's slice. Drives
     *  the section header — top level shows "$"; nested objects extend the
     *  path (["location"] → "$.location"). Pass [] (the default) for the
     *  root form; the recursive object case sets this automatically for
     *  child schema-forms. */
    @Input() pathPrefix: string[] = [];

    /** When true, this schema-form represents one column of a column-browser:
     *  • the outer .sf-group-panel chrome is suppressed (the browser provides
     *    its own column frame),
     *  • the group title + collapse toggle aren't shown,
     *  • nested-object and array-of-objects fields render as clickable nav
     *    rows that emit (openPath) instead of recursing inline. */
    @Input() columnMode = false;

    /** In columnMode, highlight the nav row whose key matches this segment
     *  (the segment the parent browser opened off this column). */
    @Input() selectedSegment: string | null = null;

    /** Emits on every valid edit. Fires synchronously with form.valueChanges. */
    @Output() valueChange = new EventEmitter<any>();

    /** Validity tracking — parents can disable Save when the form is invalid. */
    @Output() validityChange = new EventEmitter<boolean>();

    /** Emits the full JSON path of the row whose editor just received focus,
     *  e.g. "$.location.city". Used by the UserData edit page to scroll +
     *  highlight the matching node in the JSON viewer on the left. Nested
     *  schema-forms forward their child events through the parent. */
    @Output() focusPath = new EventEmitter<string>();

    /** Emits the full JSON path of a leaf the operator cleared via the
     *  per-row delete button. Bypasses the normal value-flow because
     *  FormGroup.patchValue does NOT unset keys absent from its input —
     *  so a nested clear would otherwise leave a stale value on the
     *  parent's nested group. The host listens and runs deleteAtPath
     *  directly on the JSON payload. */
    @Output() pathCleared = new EventEmitter<string>();

    /** In columnMode, emits the property key (or "[N]" index marker) the
     *  operator clicked. The parent column-browser opens the corresponding
     *  next column. */
    @Output() openPath = new EventEmitter<string>();

    private readonly fb = inject(FormBuilder);
    private readonly userDataService = inject(UserDataService);
    private readonly el: ElementRef<HTMLElement> = inject(ElementRef);

    /** Per-panel collapse state — defaults to expanded. Tracked as a signal
     *  so the template's @if reacts without a full CD pass. */
    readonly collapsed = signal<boolean>(false);

    /** Per-array-of-objects collapse state, keyed by the property name
     *  within this schema-form's fieldKeys. Each array-of-objects block
     *  renders its own peer-depth panel and tracks its own collapse. */
    private readonly arrayCollapsedMap = signal<Record<string, boolean>>({});

    toggleCollapsed(ev?: Event): void {
        ev?.stopPropagation();
        this.collapsed.update(v => !v);
    }

    isArrayCollapsed(key: string): boolean {
        return !!this.arrayCollapsedMap()[key];
    }

    toggleArrayCollapsed(key: string, ev?: Event): void {
        ev?.stopPropagation();
        this.arrayCollapsedMap.update(m => ({ ...m, [key]: !m[key] }));
    }

    form!: FormGroup;
    /** Ordered field list — derived once per schema change so the template
     *  doesn't re-sort on every change-detection pass. */
    fieldKeys: string[] = [];

    /** Cached autocomplete sources by source name, populated lazily on demand. */
    autocompleteSources: { [source: string]: string[] } = {};
    autocompleteFiltered: { [fieldKey: string]: string[] } = {};

    /** Chip-input keycodes — comma + enter, same as everywhere else in the app. */
    readonly chipSeparators = [COMMA, ENTER];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['schema']) {
            this.rebuildForm();
        }
        // Skip the value reapply when the schema also changed — rebuildForm
        // already called applyInitialValue, and calling it a second time
        // here fires the freshly-attached valueChanges subscription with the
        // initial values, which bounces back to the host as a "user edit"
        // (and historically triggered the setAtPath-overwrites-array bug).
        if (changes['value'] && !changes['schema'] && this.form) {
            this.applyInitialValue();
        }
        if (changes['disabled'] && this.form) {
            this.disabled ? this.form.disable() : this.form.enable();
        }
    }

    private rebuildForm(): void {
        if (!this.schema || this.schema.type !== 'object' || !this.schema.properties) {
            this.form = this.fb.group({});
            this.fieldKeys = [];
            return;
        }

        const props = this.schema.properties as Record<string, any>;
        const required: string[] = Array.isArray(this.schema.required) ? this.schema.required : [];

        // x-order respected; insertion order otherwise. Stable sort so equal
        // x-order values keep their declared sequence.
        this.fieldKeys = Object.keys(props).sort((a, b) => {
            const ax = typeof props[a]['x-order'] === 'number' ? props[a]['x-order'] : Infinity;
            const bx = typeof props[b]['x-order'] === 'number' ? props[b]['x-order'] : Infinity;
            return ax - bx;
        });

        const controls: { [name: string]: AbstractControl } = {};
        for (const key of this.fieldKeys) {
            const field = props[key];
            controls[key] = this.buildControl(field, required.includes(key));
        }
        this.form = this.fb.group(controls);

        this.applyInitialValue();
        this.prefetchAutocompleteSources();

        this.form.valueChanges.subscribe(v => this.valueChange.emit(this.cleanseValue(v)));
        this.form.statusChanges.subscribe(() => this.validityChange.emit(this.form.valid));
        this.validityChange.emit(this.form.valid);

        if (this.disabled) this.form.disable();
    }

    private buildControl(field: any, isRequired: boolean): AbstractControl {
        const type = field?.type;
        if (type === 'object' && field.properties) {
            // Nested object — recurse via a child SchemaFormComponent rendering.
            return this.buildNestedGroup(field, isRequired);
        }
        const readOnly = field?.readOnly === true;
        if (type === 'array') {
            return new FormControl<any[]>(
                { value: field.default ?? [], disabled: readOnly },
                this.validatorsFor(field, isRequired));
        }
        return new FormControl<any>(
            { value: field.default ?? null, disabled: readOnly },
            this.validatorsFor(field, isRequired));
    }

    private buildNestedGroup(field: any, isRequired: boolean): FormGroup {
        const props = field.properties as Record<string, any>;
        const required: string[] = Array.isArray(field.required) ? field.required : [];
        const controls: { [k: string]: AbstractControl } = {};
        for (const k of Object.keys(props)) {
            controls[k] = this.buildControl(props[k], required.includes(k));
        }
        const group = this.fb.group(controls);
        if (!isRequired) {
            // A non-required nested object is allowed to remain empty. We don't
            // attach Validators.required — required-ness on individual children
            // already drives their own validation.
        }
        return group;
    }

    private validatorsFor(field: any, isRequired: boolean): ValidatorFn[] {
        const v: ValidatorFn[] = [];
        if (isRequired) v.push(Validators.required);
        if (typeof field.minLength === 'number') v.push(Validators.minLength(field.minLength));
        if (typeof field.maxLength === 'number') v.push(Validators.maxLength(field.maxLength));
        if (typeof field.minimum === 'number')   v.push(Validators.min(field.minimum));
        if (typeof field.maximum === 'number')   v.push(Validators.max(field.maximum));
        if (typeof field.pattern === 'string')   v.push(Validators.pattern(field.pattern));
        return v;
    }

    private applyInitialValue(): void {
        if (!this.value) return;
        // patchValue silently ignores keys not in the form, which is what we
        // want — extra payload fields the schema doesn't declare just pass
        // through invisibly. emitEvent stays at default so the chip-grid
        // ControlValueAccessor receives the patch via both writeValue and
        // valueChanges. Loop protection lives at the parent level
        // (onSchemaFormChange skips no-op deep-merges) and via schema
        // memoisation (the parent returns a stable schema reference when
        // the structural shape hasn't changed), so the value-flow doesn't
        // round-trip endlessly.
        this.form.patchValue(this.value);
    }

    /** Strip null + empty-string values so the emitted payload doesn't carry
     *  ghost keys for fields the user never touched. Recursive. */
    private cleanseValue(v: any): any {
        if (v === null || v === undefined) return v;
        if (Array.isArray(v)) return v;
        if (typeof v !== 'object') return v;
        const out: any = {};
        for (const k of Object.keys(v)) {
            const cleaned = this.cleanseValue(v[k]);
            if (cleaned === null || cleaned === '' || cleaned === undefined) continue;
            if (typeof cleaned === 'object' && !Array.isArray(cleaned) && Object.keys(cleaned).length === 0) continue;
            out[k] = cleaned;
        }
        return out;
    }

    /** Look at the schema, identify fields that need their autocomplete source
     *  fetched, and fire one userDataService call per source. Cached so a
     *  schema with two userdata-types fields only hits the API once. */
    private prefetchAutocompleteSources(): void {
        const sources = new Set<string>();
        const collect = (props: Record<string, any> | undefined) => {
            if (!props) return;
            for (const key of Object.keys(props)) {
                const f = props[key];
                if (f.type === 'object') collect(f.properties);
                const src = f['x-options']?.source;
                if (src && src.startsWith('userdata-') && src !== 'userdata') sources.add(src);
            }
        };
        collect(this.schema.properties);

        for (const src of sources) {
            if (this.autocompleteSources[src]) continue;
            this.fetchAutocompleteSource(src);
        }
    }

    private fetchAutocompleteSource(src: string): void {
        const handler = {
            onStart: () => {},
            onSuccess: (rows: any) => {
                this.autocompleteSources[src] = (rows ?? []).map((r: any) => String(r));
            },
            onError: () => { this.autocompleteSources[src] = []; },
            onComplete: () => {},
        };
        switch (src) {
            case 'userdata-types':           this.userDataService.getUserDataTypes(handler); break;
            case 'userdata-departments':     this.userDataService.getDepartments(handler); break;
            case 'userdata-org-units':       this.userDataService.getOrgUnits(handler); break;
            case 'userdata-financial-units': this.userDataService.getFinancialUnits(handler); break;
            // connector-catalog is documented in the roadmap memory as
            // blocked on nucleus-connector-service; render its fields as
            // free-text inputs with a tooltip explaining the gap.
            default: this.autocompleteSources[src] = [];
        }
    }

    /** Live-filter an autocomplete source by the current control value.
     *  Cached per fieldKey so we don't allocate a new array every keystroke. */
    filteredFor(fieldKey: string, src: string): string[] {
        const all = this.autocompleteSources[src] ?? [];
        const needle = String(this.form.get(fieldKey)?.value ?? '').toLowerCase();
        if (!needle) return all;
        return all.filter(s => s.toLowerCase().includes(needle));
    }

    // ──────────────────────── widget-mapping helpers ────────────────────────

    /** Returns the canonical widget tag for a field, used by the @switch in
     *  the template. Stable per call — pure on the field object. */
    widgetFor(field: any): string {
        const t = field?.type;
        if (t === 'object' && field.properties) return 'object';
        if (t === 'array') {
            const kind = field['x-options']?.kind;
            if (kind === 'objects' || field.items?.type === 'object') {
                return 'array-objects';
            }
            // Boolean list (cardinality=list + dataType=boolean) — rendered as
            // a checkbox group rather than a free-text chip input.
            if (field.items?.type === 'boolean') return 'boolean-list';
            return 'array';
        }
        // Secret fields are masked regardless of their underlying dataType
        // (TASK-218 / capability isSecret). Checked before every other scalar
        // branch so a secret string / number / userdata key still renders as a
        // password input. Containers (object/array) are never secret.
        if (this.isSecret(field))               return 'secret';
        if (t === 'boolean')                    return 'boolean';
        if (t === 'integer' || t === 'number')  return 'number';
        // Strings — depend on enum + x-options.source
        if (Array.isArray(field?.enum))         return 'enum';
        const src = field?.['x-options']?.source;
        // A resource-leased userdata field still uses the UserDataPicker; the
        // "leased" treatment is layered on in the template via isResource().
        if (src === 'userdata')                 return 'userdata-picker';
        if (typeof src === 'string' && src.startsWith('userdata-')) return 'autocomplete';
        if (src === 'connector-catalog')        return 'connector-catalog';
        return 'text';
    }

    /** True when the field is a masked secret (capability isSecret). Read from
     *  the schema as {@code x-options.secret} (host buildSchema propagates the
     *  capability flag) with {@code x-secret} accepted as a legacy alias. */
    isSecret(field: any): boolean {
        return field?.['x-options']?.secret === true || field?.['x-secret'] === true;
    }

    /** True when the picked userdata entity will be exclusively leased via the
     *  Resource Manager for the duration of the execution (capability
     *  isResource). Read from {@code x-options.resource} ({@code x-resource}
     *  legacy alias). Only meaningful on a userdata-picker field. */
    isResource(field: any): boolean {
        return field?.['x-options']?.resource === true || field?.['x-resource'] === true;
    }

    fieldLabel(key: string, field: any): string {
        return field?.title ?? key;
    }

    fieldHint(field: any): string | null {
        return field?.description ?? null;
    }

    fieldPlaceholder(field: any): string {
        if (Array.isArray(field?.examples) && field.examples.length > 0) return String(field.examples[0]);
        return '';
    }

    /** UserData-picker filter type — extracted from x-options.filter.type so
     *  the template can pass it as `[type]`. */
    userDataPickerType(field: any): string {
        return field?.['x-options']?.filter?.type ?? '';
    }

    /** Connector field's catalog id (`<type>:<subtype>`, e.g. `datasource:jdbc`)
     *  from x-options.connectorType — drives the connection picker's filter over
     *  nucleus-connectors-service GET /connections?type=&subtype=. */
    connectorType(field: any): string {
        return field?.['x-options']?.connectorType ?? '';
    }

    /** Connector type split into the connectors-service `{type, subtype}` pair
     *  the catalog query takes as separate params. */
    connectorTypeParts(field: any): { type: string; subtype: string } {
        const id = this.connectorType(field);
        const idx = id.indexOf(':');
        return idx < 0 ? { type: id, subtype: '' } : { type: id.slice(0, idx), subtype: id.slice(idx + 1) };
    }

    asFormControl(c: AbstractControl | null): FormControl {
        return c as FormControl;
    }
    asFormGroup(c: AbstractControl | null): FormGroup {
        return c as FormGroup;
    }

    /** Path column value rendered on each row of this form — the JSON path
     *  of the *parent* containing the row's field. Top-level fields show
     *  "$"; fields under a nested form show "$.location",
     *  "$.interfaces[0].neighbor", etc. Segments beginning with "[" are
     *  array-index markers and don't get a leading "." separator. */
    rowPath(): string {
        if (!this.pathPrefix?.length) return '$';
        let p = '$';
        for (const seg of this.pathPrefix) {
            p += seg.startsWith('[') ? seg : '.' + seg;
        }
        return p;
    }

    /** Extend the current pathPrefix with one more segment — used by the
     *  template when recursing into a nested object field. */
    childPath(key: string): string[] {
        return [...(this.pathPrefix ?? []), key];
    }

    /** Full JSON path of one leaf in this form — emitted on (focusin) so
     *  the UserData edit page can highlight the matching JSON-viewer node. */
    leafPath(key: string): string {
        return this.rowPath() + '.' + key;
    }

    /** pathPrefix to pass to a child schema-form rendering one element of
     *  an array-of-objects. Includes the array key plus the bracket-index
     *  marker, so the child renders "$.interfaces[0]" as its rowPath. */
    arrayItemPath(key: string, index: number): string[] {
        return [...(this.pathPrefix ?? []), key, `[${index}]`];
    }

    /** True when this form's slice has at least one own scalar / primitive-
     *  array row. Nested-object children and array-of-objects render their
     *  own panels, so a level whose only properties are those types skips
     *  the group title (we'd otherwise stack empty headers). */
    hasOwnScalars(): boolean {
        if (!this.schema?.properties) return false;
        for (const key of this.fieldKeys ?? []) {
            const w = this.widgetFor(this.schema.properties[key]);
            if (w !== 'object' && w !== 'array-objects') return true;
        }
        return false;
    }

    /** Clear the field's value. We update the local form (for immediate
     *  UI feedback) AND emit pathCleared so the host can deleteAtPath
     *  directly on the JSON payload — the value-flow alone can't propagate
     *  a deletion across the patchValue boundary for nested fields. For
     *  container fields (object / array-of-objects), this wipes the whole
     *  branch — host's deleteAtPath removes the key entirely. */
    clearField(key: string, field: any, ev?: Event): void {
        ev?.stopPropagation();
        ev?.preventDefault();
        const ctrl = this.form.get(key);
        if (ctrl) {
            if (field?.type === 'object' && (ctrl as any).controls) {
                // FormGroup — reset() puts every child control back to its
                // default (null), then the host's deleteAtPath drops the
                // whole key from the JSON.
                (ctrl as any).reset();
            } else {
                const emptyValue =
                      field?.type === 'array'   ? []
                    : field?.type === 'boolean' ? null
                    : field?.type === 'integer' || field?.type === 'number' ? null
                    : '';
                ctrl.setValue(emptyValue, { emitEvent: false });
            }
            ctrl.markAsDirty();
            ctrl.markAsTouched();
        }
        this.pathCleared.emit(this.leafPath(key));
    }

    /** True when the field has a value worth keeping in JSON — drives the
     *  visibility of the per-row delete button. Mirrors the host's
     *  isEmpty check in onSchemaFormChange (null / undefined / "" / [] are
     *  "absent"; 0 / false are "present"). For object containers, returns
     *  true when any nested key has a non-empty value (so the operator can
     *  delete the whole branch with one click). */
    hasValue(key: string): boolean {
        const v = this.form.get(key)?.value;
        if (v === null || v === undefined) return false;
        if (typeof v === 'string' && v === '') return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') {
            return Object.keys(v).filter(k => {
                const x = v[k];
                return x !== null && x !== '' && x !== undefined
                    && !(Array.isArray(x) && x.length === 0);
            }).length > 0;
        }
        return true;
    }

    /** True when the field is declared read-only on its UserDataType — the
     *  control is rendered but disabled, and the delete icon is hidden so
     *  the operator can't clear it from the JSON. The flag is read from
     *  the schema (propagated by the host's buildSchema from the type's
     *  attribute definition). */
    isReadOnly(field: any): boolean {
        return field?.readOnly === true || this.disabled;
    }

    /** Nesting depth — drives the panel's background tint so each level
     *  reads visually distinct from its parent. */
    get depth(): number {
        return this.pathPrefix?.length ?? 0;
    }

    /** Short preview text rendered on a container nav row (columnMode) —
     *  "4 items" for arrays, "5 fields" for objects with content. */
    containerPreview(key: string, field: any): string {
        const v = this.form.get(key)?.value;
        if (Array.isArray(v)) return v.length === 1 ? '1 item' : `${v.length} items`;
        if (v && typeof v === 'object') {
            const count = Object.keys(v).filter(k => {
                const x = v[k];
                return x !== null && x !== '' && x !== undefined
                    && !(Array.isArray(x) && x.length === 0);
            }).length;
            if (count === 0) return '(empty)';
            return count === 1 ? '1 field' : `${count} fields`;
        }
        return '';
    }

    /** Material icon for the leaf's nav-row affordance in columnMode. */
    leafIcon(field: any): string {
        const t = field?.type;
        if (t === 'boolean') return 'toggle_on';
        if (t === 'integer' || t === 'number') return 'pin';
        if (t === 'array') return 'list_alt';
        if (Array.isArray(field?.enum)) return 'tune';
        const src = field?.['x-options']?.source;
        if (src === 'userdata') return 'travel_explore';
        if (typeof src === 'string' && src.startsWith('userdata-')) return 'manage_search';
        return 'edit';
    }

    /** Short value preview for a leaf in columnMode — appears next to the
     *  field name on the nav row so the operator sees the current value
     *  without having to open the editor column. Returns an empty string
     *  for empty values (don't add "(empty)" noise). */
    leafPreview(key: string, field: any): string {
        const v = this.form.get(key)?.value;
        if (v === null || v === undefined || v === '') return '';
        if (Array.isArray(v)) {
            if (v.length === 0) return '';
            if (v.length <= 3) return v.join(', ');
            return `${v.slice(0, 2).join(', ')} + ${v.length - 2} more`;
        }
        if (typeof v === 'boolean') return v ? 'true' : 'false';
        const s = String(v);
        return s.length > 32 ? s.slice(0, 30) + '…' : s;
    }

    /** Short preview for a container nav row — empty string instead of
     *  "(empty)" so empty containers don't shout. */
    containerPreviewBrief(key: string, field: any): string {
        const v = this.form.get(key)?.value;
        if (Array.isArray(v)) {
            if (v.length === 0) return '';
            return v.length === 1 ? '1 item' : `${v.length} items`;
        }
        if (v && typeof v === 'object') {
            const count = Object.keys(v).filter(k => {
                const x = v[k];
                return x !== null && x !== '' && x !== undefined
                    && !(Array.isArray(x) && x.length === 0);
            }).length;
            if (count === 0) return '';
            return count === 1 ? '1 field' : `${count} fields`;
        }
        return '';
    }

    // ──────────────────────── array-of-primitives helpers ───────────────────

    arrayValue(key: string): string[] {
        const v = this.form.get(key)?.value;
        if (!Array.isArray(v)) return [];
        // Coerce every item to a string so mat-chip-row's display path is
        // happy with primitive arrays of integers / booleans.
        return v.map(item => item === null || item === undefined ? '' : String(item));
    }

    // ── boolean-list helpers (cardinality=list + dataType=boolean) ───────────

    /** Options offered by a boolean-list field. Each option is a labelled flag
     *  the operator can toggle on/off; the control value is the array of the
     *  *checked* option values. Sources, in priority order:
     *    • field['x-options'].options : [{ value, label }]
     *    • field.items.enum           : [v1, v2, ...] (label === value)
     *  When neither is present the field degrades to a single "Enabled" flag. */
    booleanListOptions(field: any): { value: string; label: string }[] {
        const opts = field?.['x-options']?.options;
        if (Array.isArray(opts) && opts.length) {
            return opts.map((o: any) =>
                typeof o === 'object' && o !== null
                    ? { value: String(o.value), label: String(o.label ?? o.value) }
                    : { value: String(o), label: String(o) });
        }
        const en = field?.items?.enum;
        if (Array.isArray(en) && en.length) {
            return en.map((v: any) => ({ value: String(v), label: String(v) }));
        }
        return [{ value: 'true', label: field?.title ?? 'Enabled' }];
    }

    /** True when {@code optionValue} is in the field's current array value. */
    isBooleanListChecked(key: string, optionValue: string): boolean {
        const v = this.form.get(key)?.value;
        return Array.isArray(v) && v.map((x: any) => String(x)).includes(optionValue);
    }

    /** Add / remove an option value from the field's array on checkbox toggle.
     *  Emits a real array so the form value carries the selected flags. */
    onBooleanListToggle(key: string, optionValue: string, ev: MatCheckboxChange): void {
        const ctrl = this.form.get(key);
        if (!ctrl) return;
        const current: string[] = Array.isArray(ctrl.value) ? ctrl.value.map((x: any) => String(x)) : [];
        const next = ev.checked
            ? (current.includes(optionValue) ? current : [...current, optionValue])
            : current.filter(x => x !== optionValue);
        ctrl.setValue(next);
        ctrl.markAsDirty();
    }

    // ── array-of-objects helpers ────────────────────────────────────────────

    arrayObjectsValue(key: string): any[] {
        const v = this.form.get(key)?.value;
        return Array.isArray(v) ? v : [];
    }

    /** Replace the array's i-th element with the value the sub-form just
     *  emitted. Skips when the new value deep-equals the existing one
     *  to break the patchValue → valueChange → patchValue loop. */
    onArrayObjectItemChange(key: string, index: number, newValue: any): void {
        const arr = [...this.arrayObjectsValue(key)];
        if (JSON.stringify(arr[index]) === JSON.stringify(newValue)) return;
        arr[index] = newValue;
        this.form.get(key)?.setValue(arr, { emitEvent: true });
    }

    addArrayObjectItem(key: string, field: any, ev?: Event): void {
        ev?.stopPropagation();
        ev?.preventDefault();
        const arr = [...this.arrayObjectsValue(key), {}];
        this.form.get(key)?.setValue(arr, { emitEvent: true });
        // Make sure this form's panel is expanded so the new item is visible
        // even if the operator had collapsed the group earlier.
        this.collapsed.set(false);
        const newIndex = arr.length - 1;
        const newPath = `${this.leafPath(key)}[${newIndex}]`;
        // Wait for Angular's @for + child schema-form to render before
        // measuring; rAF is the simplest hook that lands after view-update.
        requestAnimationFrame(() => {
            const target = this.el.nativeElement.querySelector(
                `.sf-group-panel[data-path="${newPath}"]`
            ) as HTMLElement | null;
            if (target) {
                target.scrollIntoView({ block: 'center', behavior: 'smooth' });
                // Focus the first input inside the new item so the operator
                // can start typing immediately.
                const firstInput = target.querySelector(
                    'input, textarea, mat-select, [tabindex]:not([tabindex="-1"])'
                ) as HTMLElement | null;
                firstInput?.focus({ preventScroll: true });
            }
        });
    }

    removeArrayObjectItem(key: string, index: number, ev?: Event): void {
        ev?.stopPropagation();
        ev?.preventDefault();
        const arr = this.arrayObjectsValue(key).filter((_, i) => i !== index);
        this.form.get(key)?.setValue(arr, { emitEvent: true });
    }

    arrayAdd(key: string, event: MatChipInputEvent): void {
        const v = (event.value ?? '').trim();
        if (!v) return;
        const ctrl = this.form.get(key);
        if (!ctrl) return;
        const next = [...this.arrayValue(key), v];
        ctrl.setValue(next);
        event.chipInput?.clear();
    }

    arrayRemove(key: string, item: string): void {
        const ctrl = this.form.get(key);
        if (!ctrl) return;
        ctrl.setValue(this.arrayValue(key).filter(x => x !== item));
    }
}
